import { useCallback, useEffect, useRef, useState } from 'react';

import type React from 'react';

export type UseHoverIntentOpts = {
  /** Grace period before closing (ms). */
  delay: number;
  /**
   * Dwell period before opening (ms). The timer restarts on every mousemove,
   * so the target only commits once the cursor RESTS on it — a cursor
   * sweeping across trigger elements (e.g. reading the transcript) never
   * opens. Once open, the target follows the cursor without delay. Omit or
   * pass 0 to open immediately.
   */
  openDelay?: number;
  /**
   * Resolve a target element from the event target. Return null if nothing
   * meaningful is hovered. The MouseEvent is passed so the selector can
   * gate on cursor coordinates (e.g. trigger only when the cursor is in a
   * specific Y-band of the target — PLAT-10045 uses this to require that
   * the cursor be over the overlay-match underline mark rather than the
   * word text itself).
   */
  selector: (
    target: HTMLElement,
    event: React.MouseEvent,
  ) => HTMLElement | null;
  /** Optional: dismiss when this element scrolls. */
  scrollContainer?: () => HTMLElement | null | undefined;
  /** Optional: selector string for the popover element itself (to ignore mousemove events from it). */
  popoverSelector?: string;
};

export type UseHoverIntentResult = {
  /** The currently resolved target element, or null. */
  targetEl: HTMLElement | null;
  /**
   * The element under the cursor that the selector resolved, with no dwell
   * delay — use for instant hover feedback (e.g. brightening a range) while
   * `targetEl` waits out `openDelay`. Identical to `targetEl` when
   * `openDelay` is unset.
   */
  hoverEl: HTMLElement | null;
  /** Call on mousemove over the container. */
  onMouseMove: (e: React.MouseEvent) => void;
  /** Call on mousedown (dismisses + pauses during selection). */
  onMouseDown: (e: React.MouseEvent) => void;
  /**
   * Call on mouseup (resumes after selection). A plain left click on a
   * trigger — press + release with no drag-selection — opens the target
   * immediately, bypassing `openDelay`; a release anywhere else dismisses.
   */
  onMouseUp: (e?: React.MouseEvent) => void;
  /** Call on mouseleave from the container. */
  onMouseLeave: () => void;
  /** Pass to popover's onMouseEnter — cancels the close timer. */
  cancelClose: () => void;
  /** Pass to popover's onMouseLeave — re-arms the close timer. */
  armClose: () => void;
  /** Imperatively dismiss (e.g., on scroll, toggle off). */
  dismiss: () => void;
};

/**
 * Generic hover-intent hook. Resolves a target element via a selector function
 * and manages a grace-period timer for showing/hiding overlays.
 *
 * Has NO knowledge of any domain concepts (quotes, clips, highlights, etc.).
 */
export function useHoverIntent(opts: UseHoverIntentOpts): UseHoverIntentResult {
  const {
    delay,
    openDelay = 0,
    selector,
    scrollContainer,
    popoverSelector,
  } = opts;

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [hoverEl, setHoverEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingOpenRef = useRef<HTMLElement | null>(null);
  const isSelectingRef = useRef(false);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const cancelOpen = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    pendingOpenRef.current = null;
  }, []);

  const armClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setTargetEl(null);
      closeTimerRef.current = null;
    }, delay);
  }, [delay]);

  const dismiss = useCallback(() => {
    cancelClose();
    cancelOpen();
    setTargetEl(null);
    setHoverEl(null);
  }, [cancelClose, cancelOpen]);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isSelectingRef.current) return;

      const target = e.target as HTMLElement | null;
      if (!target) {
        setHoverEl(null);
        cancelOpen();
        // Don't re-arm if already counting down — armClose resets its timer,
        // so re-arming on every move would keep the popover open for as long
        // as the cursor is moving, not `delay` after it left the trigger.
        if (targetEl && !closeTimerRef.current) armClose();
        return;
      }

      // Ignore events bubbling from the popover itself (portaled elements
      // bubble through React's synthetic event tree).
      if (popoverSelector && target.closest(popoverSelector)) {
        return;
      }

      const resolved = selector(target, e);
      if (!resolved) {
        setHoverEl(null);
        cancelOpen();
        if (targetEl && !closeTimerRef.current) armClose();
        return;
      }

      setHoverEl(prev => (prev === resolved ? prev : resolved));
      cancelClose();

      // Already open (or no dwell configured): follow the cursor directly.
      if (!openDelay || targetEl) {
        cancelOpen();
        setTargetEl(prev => {
          if (prev === resolved) return prev;
          return resolved;
        });
        return;
      }

      // Dwell: restart the timer on every move so the target only commits
      // once the cursor rests — a sweeping cursor never opens.
      pendingOpenRef.current = resolved;
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        setTargetEl(pendingOpenRef.current);
        pendingOpenRef.current = null;
      }, openDelay);
    },
    [
      selector,
      popoverSelector,
      targetEl,
      armClose,
      cancelClose,
      cancelOpen,
      openDelay,
    ],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // Don't dismiss if clicking inside the popover
      if (popoverSelector && target?.closest(popoverSelector)) {
        return;
      }
      isSelectingRef.current = true;
      // Pressing a trigger is (so far) a click to open it, not a dismiss —
      // holding the current target avoids flashing an already-open overlay
      // closed and straight back open on mouseup. If the press turns out to
      // be the start of a drag-selection, mouseup dismisses instead.
      if (e.button === 0 && target && selector(target, e)) {
        cancelClose();
        cancelOpen();
        return;
      }
      dismiss();
    },
    [popoverSelector, dismiss, selector, cancelClose, cancelOpen],
  );

  const onMouseUp = useCallback(
    (e?: React.MouseEvent) => {
      isSelectingRef.current = false;
      if (!e || e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (popoverSelector && target.closest(popoverSelector)) return;

      // A release that leaves text selected ended a drag, not a click.
      const resolved =
        document.getSelection()?.isCollapsed === false
          ? null
          : selector(target, e);
      if (!resolved) {
        dismiss();
        return;
      }

      // Click = intent, so open now rather than making the user wait out the
      // dwell they already skipped past.
      cancelOpen();
      cancelClose();
      setHoverEl(resolved);
      setTargetEl(resolved);
    },
    [popoverSelector, selector, dismiss, cancelClose, cancelOpen],
  );

  const onMouseLeave = useCallback(() => {
    setHoverEl(null);
    cancelOpen();
    armClose();
  }, [armClose, cancelOpen]);

  // Dismiss on scroll
  useEffect(() => {
    const container = scrollContainer?.();
    if (!container) return;
    const onScroll = () => dismiss();
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [scrollContainer, dismiss]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
      }
    };
  }, []);

  return {
    targetEl,
    hoverEl,
    onMouseMove,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    cancelClose,
    armClose,
    dismiss,
  };
}
